'use client';

import sdk from '@/client/api/sdk';
import { Global } from '@emotion/react';
import { Box, Button, IconButton, SwipeableDrawer, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslate } from '@tolgee/react';
import { Check, Copy, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CalculatorData } from '../home/home-earnings-calculator';
import CibNvidia from '../icons/CibNvidia';
import CibWindows from '../icons/CibWindows';

interface ReqTooltipProps {
  onClick?: () => void;
  showDrawer?: boolean;
  onDrawerClose?: () => void;
  downloadUrl: string;
  buttonType?: 'neon' | 'cyber';
}

// 顶部拖动条组件
const Puller = styled('div')(() => ({
  width: 30,
  height: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
}));

const ReqTooltip = ({
  onClick,
  showDrawer = false,
  onDrawerClose,
  downloadUrl,
  buttonType = 'cyber',
}: ReqTooltipProps) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDrawerOpen, setDrawerOpen] = useState(showDrawer);
  const [copied, setCopied] = useState(false);
  const [calculatorData, setCalculatorData] = useState<CalculatorData | null>(null);

  useEffect(() => {
    const fetchCalculatorData = async () => {
      const resp = await sdk.GET('/api/client/calculator/data');
      if (resp.data?.data) {
        setCalculatorData(resp.data.data);
      }
    };
    fetchCalculatorData();
  }, []);

  // 监听 showDrawer
  useEffect(() => {
    setDrawerOpen(showDrawer);
  }, [showDrawer]);

  // 根据按钮类型选择样式
  const tagStyle =
    buttonType === 'neon' ? 'bg-color-primary/20 text-color-primary' : 'bg-color-secondary/20 text-color-secondary';

  // 处理复制
  const handleCopy = () => {
    navigator.clipboard.writeText(downloadUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 内容渲染函数
  const renderContent = (showDownload = false) => (
    <>
      <div className="relative px-4 py-5">
        <div className="flex flex-col gap-1">
          {/* 标题 */}
          <div className="mb-3">
            <div className="flex items-center justify-center mb-1">
              <Box component={CibWindows} width={20} height={20} style={{ filter: 'brightness(0) invert(1)' }} />
              <Plus size={16} className="mx-2 text-white" />
              <Box component={CibNvidia} width={20} height={20} style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
            <h3
              className="text-center text-[1rem] font-bold tracking-wider text-white"
              style={{ textTransform: 'none' }}
            >
              {t('provider_page.tooltip.windows_nvidia_required')}
            </h3>
          </div>

          <div className="grid grid-cols-3 gap-x-1 gap-y-1">
            {calculatorData?.gpuFamily.map((gpu) => (
              <div key={gpu} className="flex items-center text-[0.7rem] text-n-2" style={{ textTransform: 'none' }}>
                <span className="mr-1.5 h-1 w-1 rounded-full bg-color-primary" />
                {gpu}
              </div>
            ))}
          </div>
        </div>

        {/* 装饰线 */}
        <span className="absolute left-[10%] right-[10%] top-[8px] h-[1px] bg-gradient-to-r from-transparent via-color-secondary/40 to-transparent" />
        <span className="absolute bottom-[8px] left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-color-secondary/40 to-transparent" />
      </div>

      {/* 下载部分 */}
      {showDownload && (
        <div className="mt-3 px-4 pb-24">
          <h3
            className="text-center text-[1rem] font-bold tracking-wider text-white mb-2"
            style={{ textTransform: 'none' }}
          >
            {t('provider_page.tooltip.download_title')}
          </h3>

          <Box
            sx={{
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '8px',
              padding: '4px 8px',
              border: '1px solid rgba(255,255,255,0.1)',
              wordBreak: 'break-all',
              mb: 1.5,
              '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <span className="text-[0.7rem] text-white/90">{downloadUrl}</span>
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={copied ? <Check size={18} /> : <Copy size={18} />}
            onClick={handleCopy}
            sx={{
              height: 44,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              background: 'linear-gradient(135deg, rgba(149, 93, 245, 1) 0%, rgba(250, 84, 28, 1) 100%)',
              boxShadow: '0 4px 20px rgba(149, 93, 245, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(149, 93, 245, 0.9) 10%, rgba(250, 84, 28, 0.9) 90%)',
                boxShadow: '0 6px 25px rgba(149, 93, 245, 0.6)',
              },
            }}
          >
            {copied ? t('provider_page.tooltip.copied') : t('provider_page.tooltip.copy_link')}
          </Button>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* 移动端抽屉 */}
      {isMobile && (
        <>
          <Global
            styles={{
              '.MuiDrawer-root > .MuiPaper-root': {
                overflow: 'visible',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
              },
            }}
          />
          <SwipeableDrawer
            anchor="bottom"
            open={isDrawerOpen}
            onClose={() => {
              setDrawerOpen(false);
              onDrawerClose?.();
            }}
            onOpen={() => setDrawerOpen(true)}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(17, 19, 26, 0.95)',
                backdropFilter: 'blur(10px)',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                padding: '2px',
                background:
                  'linear-gradient(rgba(17, 19, 26, 0.95), rgba(17, 19, 26, 0.95)) padding-box, linear-gradient(225deg, #6F43F3, #FF8C19) border-box',
                border: '2px solid transparent',
                borderBottom: 'none',
              },
            }}
          >
            <Box sx={{ position: 'relative', pt: 3 }}>
              <Puller />
              <IconButton
                onClick={() => {
                  setDrawerOpen(false);
                  onDrawerClose?.();
                }}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 12,
                  top: 12,
                  color: '#fff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                  zIndex: 10,
                }}
              >
                <X size={18} />
              </IconButton>
              {renderContent(true)}
            </Box>
          </SwipeableDrawer>

          {/* 移动端标签 */}
          <span
            className={`text-[0.65rem] px-1.5 py-0.5 rounded-sm font-bold tracking-wider cursor-pointer ${tagStyle}`}
            style={{ textTransform: 'none' }}
            onClick={() => {
              setDrawerOpen(true);
              onClick?.();
            }}
          >
            REQ
          </span>
        </>
      )}

      {/* 桌面端悬浮框 */}
      {!isMobile && (
        <Tooltip
          title={
            <Box
              sx={{
                p: 0,
                bgcolor: 'transparent',
                width: 'fit-content',
              }}
            >
              {renderContent()}
            </Box>
          }
          placement="right"
          followCursor
          arrow
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'rgba(17, 19, 26, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                borderRadius: '12px',
                padding: '2px',
                maxWidth: 'none',
                width: 'fit-content',
                background:
                  'linear-gradient(rgba(17, 19, 26, 0.95), rgba(17, 19, 26, 0.95)) padding-box, linear-gradient(135deg, #6F43F3, #FF8C19) border-box',
                border: '2px solid transparent',
                '& .MuiTooltip-arrow': {
                  color: 'rgba(17, 19, 26, 0.95)',
                  '&::before': {
                    background: 'linear-gradient(135deg, #6F43F3, #FF8C19)',
                    border: 'none',
                  },
                },
              },
            },
          }}
        >
          <span
            className={`text-[0.65rem] px-1.5 py-0.5 rounded-sm font-bold tracking-wider cursor-help ${tagStyle}`}
            style={{ textTransform: 'none' }}
            onClick={onClick}
          >
            REQ
          </span>
        </Tooltip>
      )}
    </>
  );
};

export default ReqTooltip;
